/**
 * Doctor Now appointment picker (board card 28373).
 *
 * The step between intake and checkout for a DoctorNow product whose
 * `doctor_now_config.requires_appointment` is `true`. Every OTHER DoctorNow product is async
 * — there is no slot to book — and `resolvePostLabRoute` still sends those to `/checkout`.
 *
 * ⚠️ A deprecated sibling picker used to fabricate its slots with `Math.random()`; card 40756
 * (2026-09-15) deleted it, together with the dead redirect route that was its last mention.
 * This page was always unrelated to it: every time offered HERE comes from
 * `GET /api/doctor-now/availability/slots`, the proxy card 28372 shipped, which reads the
 * vendor's REAL availability and never lets a credential reach the browser.
 *
 * 🔑 A VENDOR THAT ANSWERS NOTHING MUST NOT TRAP THE CUSTOMER. That endpoint answers
 * `200 {slots: []}` for a vendor failure AND for a patient with no chart yet — the chart is
 * created by unified intake AT CHECKOUT, so a first-time patient legitimately has none (15 of
 * 6293 profiles had one when 28372 measured it). Both land on the "no times available" path
 * below, which STILL reaches `/checkout`.
 *
 * The slot travels as the EXISTING `appointmentTime` router state (`CheckoutPage.jsx:27`),
 * which already posts `preferredAppointmentTime` at `:688` — ⛔ deliberately not a new field.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import * as productService from '../../services/productService';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  CHECKOUT_ROUTE,
  resolvePostLabRoute,
  requiresDoctorNowAppointment,
  readDoctorNowEncounterTypeId
} from '../../utils/intakeRouting';
import styles from './DoctorNowSchedulePage.module.css';

/** Mirrors `DEFAULT_SCHEDULING_TIMEZONE` in `api/services/external/doctorNowService.js:938`. */
const SCHEDULING_TIMEZONE = 'America/New_York';
/**
 * The EXACT label the shipped producer writes for this zone (`CalendarDemo.jsx:6`), so both
 * scheduling paths put ONE value into `appointmentTime.timezone`. ⚠️ It says EST year-round —
 * a pre-existing inaccuracy in that table, matched deliberately: diverging silently would make
 * the two paths disagree on the same checkout row.
 */
const TIMEZONE_LABEL = 'Eastern (EST)';
const DAYS_OFFERED = 5;

/** Pure. One `Intl` formatter, curried — the only place a locale/zone pair is built. */
const formatterFor = (locale, timeZone, options) =>
  (value) => new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(value);

/** Pure. `YYYY-MM-DD` for an instant, in the clinic's zone (`en-CA`'s short date IS that order). */
const toDateKey = formatterFor('en-CA', SCHEDULING_TIMEZONE, { year: 'numeric', month: '2-digit', day: '2-digit' });

/** Pure. `10:00 AM` for an instant, in the clinic's zone. */
const formatClockTime = formatterFor('en-US', SCHEDULING_TIMEZONE, { hour: 'numeric', minute: '2-digit', hour12: true });

/** Pure. `Wed, Sep 17` — in UTC, because a date KEY is already a calendar date, not an instant. */
const formatUtcDayLabel = formatterFor('en-US', 'UTC', { weekday: 'short', month: 'short', day: 'numeric' });

/** Pure. A date key as a real (UTC-anchored) date, so calendar arithmetic cannot drift. */
const keyToUtcDate = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

/**
 * Pure. Arithmetic in UTC on purpose: stepping a real INSTANT by 86_400_000 ms across a DST
 * boundary lands on the same wall-clock date twice (or skips one) — a duplicate day tab.
 */
const addDays = (dateKey, days) =>
  new Date(keyToUtcDate(dateKey).getTime() + days * 86400000).toISOString().slice(0, 10);

const buildDateKeys = (startKey, count) =>
  Array.from({ length: count }, (_, index) => addDays(startKey, index));

const formatDayLabel = (dateKey) => formatUtcDayLabel(keyToUtcDate(dateKey));

/**
 * Pure. `10:00 AM` for a slot. ⛔ Deliberately NOT the endpoint's own `label`: that field falls
 * back to the raw `start` instant (`transformAvailabilitySlot`), so a vendor payload without
 * `display_time` would paint an ISO string on a button.
 */
const formatSlotTime = (scheduledStart) => formatClockTime(new Date(scheduledStart));

/**
 * Pure. `September 16, 2026` — the EXACT shape `CalendarDemo`'s `formatDateForApi` emits.
 * 🪤 ⛔ NO WEEKDAY: `CheckoutPage.jsx:1411` prepends its own, so the tab format `Wed, Sep 16`
 * painted "Wednesday, Wed, Sep 16". Tabs and this field are two formats on purpose.
 */
const formatCheckoutDate = formatterFor('en-US', SCHEDULING_TIMEZONE, { month: 'long', day: 'numeric', year: 'numeric' });

/** Pure. The EXACT router state `CheckoutPage` consumes — it posts `utcTime`, prints the rest. */
const buildAppointmentTime = (scheduledStart) => ({
  utcTime: new Date(scheduledStart).toISOString(),
  date: formatCheckoutDate(new Date(scheduledStart)),
  time: formatSlotTime(scheduledStart),
  timezone: TIMEZONE_LABEL
});

/** Pure. The first offered day that actually has a bookable slot, or null. */
const findFirstDayWithSlots = (slotsByDate, dateKeys) =>
  dateKeys.find((key) => (slotsByDate[key]?.length ?? 0) > 0) ?? null;

const DoctorNowSchedulePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('product');

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [activeDate, setActiveDate] = useState(null);
  const [selectedStart, setSelectedStart] = useState(null);

  /**
   * Derived once at mount, ⛔ not held in state: re-deriving inside the fetch would make the
   * rendered tabs and the fetched keys two authorities that can disagree.
   */
  const dateKeys = useMemo(() => buildDateKeys(toDateKey(new Date()), DAYS_OFFERED), []);

  /**
   * Rebuild the entry query string so the deep-link contract `IntakePage` forwards
   * (`product`/`supplyMonths`/`dosage`) survives this hop. Same shape as `LabUploadPage`.
   */
  const onwardQuery = useCallback((productSlug) => {
    const supplyMonths = searchParams.get('supplyMonths') ?? sessionStorage.getItem('selectedSupplyMonths') ?? '';
    const dosage = searchParams.get('dosage') ?? sessionStorage.getItem('selectedDosageId') ?? '';
    return [
      `product=${encodeURIComponent(productSlug)}`,
      supplyMonths ? `supplyMonths=${encodeURIComponent(supplyMonths)}` : '',
      dosage ? `dosage=${encodeURIComponent(dosage)}` : ''
    ].filter(Boolean).join('&');
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      if (!slug) {
        navigate('/catalog', { replace: true });
        return;
      }
      try {
        const response = await productService.getProductBySlug(slug);
        if (cancelled) return;
        const loaded = response?.product ?? null;
        setProduct(loaded);

        // Same shape as `LabUploadPage`'s stale-link guard: this step exists only for an
        // appointment-required DoctorNow product, so anything else is handed straight back to
        // the route it would have taken — never strand a customer on a picker it does not need.
        if (loaded && !requiresDoctorNowAppointment(loaded)) {
          navigate(`${resolvePostLabRoute(loaded)}?${onwardQuery(loaded.slug)}`, { replace: true });
        }
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Could not load this product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProduct();
    return () => { cancelled = true; };
  }, [slug, navigate, onwardQuery, toast]);

  useEffect(() => {
    let cancelled = false;
    const encounterTypeId = readDoctorNowEncounterTypeId(product);
    if (!product || !requiresDoctorNowAppointment(product)) return undefined;

    const loadSlots = async () => {
      if (!encounterTypeId) {
        // No encounter type means there is nothing to ask the vendor ABOUT. Fall to the
        // same empty path rather than firing five requests the route will 400.
        if (!cancelled) setLoadingSlots(false);
        return;
      }
      const days = await Promise.all(dateKeys.map((date) =>
        api.get('/doctor-now/availability/slots', { params: { encounterTypeId, date } })
          .then((response) => ({ date, slots: Array.isArray(response?.slots) ? response.slots : [] }))
          // A failed lookup is ONE empty day, not a dead page — criterion 5's failure arm.
          .catch(() => ({ date, slots: [] }))
      ));
      if (cancelled) return;
      const next = days.reduce((acc, { date, slots }) => ({ ...acc, [date]: slots }), {});
      setSlotsByDate(next);
      setActiveDate(findFirstDayWithSlots(next, dateKeys));
      setLoadingSlots(false);
    };

    loadSlots();
    return () => { cancelled = true; };
  }, [product, dateKeys]);

  const goToCheckout = (appointmentTime) =>
    navigate(`${CHECKOUT_ROUTE}?${onwardQuery(product.slug)}`, { state: { appointmentTime } });

  const handleContinue = () => {
    if (!selectedStart) return;
    goToCheckout(buildAppointmentTime(selectedStart));
  };

  if (loading) return <LoadingSpinner fullScreen />;

  if (!product) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Product not found</h1>
          <p className={styles.subtitle}>We could not load this treatment. Please choose it again from the catalog.</p>
          <button type="button" className={styles.continueBtn} onClick={() => navigate('/catalog')}>Back to catalog</button>
        </div>
      </div>
    );
  }

  const offeredSlots = activeDate ? (slotsByDate[activeDate] ?? []) : [];
  const hasAnySlots = dateKeys.some((key) => (slotsByDate[key]?.length ?? 0) > 0);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Choose your appointment</h1>
        <p className={styles.subtitle}>
          {product.name} includes a visit with a licensed provider. Pick a time that works for you —
          all times are shown in {TIMEZONE_LABEL} time.
        </p>

        {loadingSlots && <p className={styles.loadingNote}>Finding available times…</p>}

        {!loadingSlots && hasAnySlots && (
          <>
            <div className={styles.dayTabs} role="tablist" aria-label="Available days">
              {dateKeys.map((key) => {
                const count = slotsByDate[key]?.length ?? 0;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={key === activeDate}
                    disabled={count === 0}
                    className={`${styles.dayTab} ${key === activeDate ? styles.dayTabActive : ''}`}
                    onClick={() => { setActiveDate(key); setSelectedStart(null); }}
                  >
                    <span className={styles.dayTabLabel}>{formatDayLabel(key)}</span>
                    <span className={styles.dayTabCount}>{count === 0 ? 'No times' : `${count} available`}</span>
                  </button>
                );
              })}
            </div>

            <div className={styles.slotGrid}>
              {offeredSlots.map((slot) => (
                <button
                  key={slot.scheduledStart}
                  type="button"
                  aria-pressed={slot.scheduledStart === selectedStart}
                  className={`${styles.slot} ${slot.scheduledStart === selectedStart ? styles.slotSelected : ''}`}
                  onClick={() => setSelectedStart(slot.scheduledStart)}
                >
                  {formatSlotTime(slot.scheduledStart)}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={styles.continueBtn}
              onClick={handleContinue}
              disabled={!selectedStart}
            >
              Continue
            </button>
          </>
        )}

        {!loadingSlots && !hasAnySlots && (
          <div className={styles.emptyState} role="status">
            <p className={styles.emptyTitle}>No times available — we&rsquo;ll reach out</p>
            <p className={styles.emptyBody}>
              We could not find an opening in the next {DAYS_OFFERED} days. Continue to checkout and our
              care team will contact you to schedule your visit.
            </p>
            <button type="button" className={styles.continueBtn} onClick={() => goToCheckout(null)}>
              Continue to checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorNowSchedulePage;
